import { Autocomplete, TextField } from "@mui/material";
import { DeleteButton } from "Common/Components/Buttons/DeleteButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { showCustomSwal } from "helpers/swalHelper";
import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchUOMByDropDown } from "slices/Masters/unit/thunk";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import * as Yup from "yup";

interface Item {
  product_id: string;
  model_id: string;
  unit_id: string;
  unit_price: number | string;
  lead_time: number | string;
  capacity: number | string;
}

interface ModelOption {
  model_id: string;
  model_name: string;
}

interface ProductOption {
  product_id: string;
  product_name: string;
  unit_name: string;
  unit_price: string;
}

interface UnitOption {
  unit_id: string;
  unit_name: string;
}

const ProductDetails: React.FC<any> = ({
  goToTab,
  products,
  setProducts,
  final_submit,
}) => {
  const dispatch = useDispatch<any>();
  const [dropDownLoading, setDropDownLoading] = useState<boolean>(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [unitOptions, setUnitOptions] = useState<UnitOption[]>([]);
  // ✅ Formik setup
  const formik = useFormik({
    initialValues: {
      model: null,
      product: null,
      unit: null,
      unit_price: "",
      lead_time: "",
      capacity: "",
    },
    validationSchema: Yup.object({
      model: Yup.object().nullable().required("Model is required"),
      product: Yup.object().nullable().required("Product is required"),
      unit: Yup.string().nullable(),
      unit_price: Yup.number()
        .typeError("Unit Price must be a number")
        .positive("Unit Price must be greater than 0")
        .required("Unit Price is required"),
      lead_time: Yup.number()
        .typeError("Lead Time must be a number")
        .positive("Lead Time must be greater than 0")
        .required("Lead Time is required"),
      capacity: Yup.number()
        .typeError("Capacity must be a number")
        .positive("Capacity must be greater than 0")
        .required("Capacity is required"),
    }),
    onSubmit: (values, { resetForm }) => {
      const selectedProduct = values.product as ProductOption | null;
      if (
        products?.some(
          (item: Item) => item?.product_id === selectedProduct?.product_id,
        )
      ) {
        showCustomSwal({
          icon: "warning",
          text: "Item already added",
        });
        return;
      }

      const payload: any = {
        ...values,
        product_id: (values?.product as any)?.product_id,
        unit_id: (values?.product as any)?.unit_id,
        lead_time: Number(values?.lead_time),
        unit_price: Number(values?.unit_price),
        capacity: Number(values?.capacity),
      };
      setProducts((prev: any) => [...prev, payload]);
      resetForm();
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  // ✅ Delete logic
  const handleDelete = (index: number) => {
    const new_item_units = products.filter(
      (_item: Item, i: number) => i !== index,
    );
    setProducts(new_item_units);
  };

  useEffect(() => {
    async function loadDropdowns() {
      setDropDownLoading(true);
      try {
        const [uomRes, modelRes] = await Promise.all([
          dispatch(fetchUOMByDropDown()).unwrap(),
          dispatch(fetchModelByDropDown()).unwrap(),
        ]);
        setUnitOptions(uomRes?.data || []);
        setModelOptions(modelRes?.data || []);
      } catch (error) {
        toast.error("Failed to load dropdown data");
      } finally {
        setDropDownLoading(false);
      }
    }
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formik?.values?.model && (formik?.values?.model as any)?.model_id) {
      const fetchProducts = async () => {
        const response = await dispatch(
          fetchProductsDropDownByModel(
            (formik?.values?.model as any)?.model_id as string
          )
        ).unwrap();
        setProductOptions(response?.data || []);
        console.log("response of product", response?.data);
      };
      fetchProducts();
    } else {
      setProductOptions([]);
    }
  }, [(formik?.values?.model as any)?.model_id]);

  return (
    <div>
      <div className="mb-2 mt-2" style={{ overflowX: "auto" }}>
        <Form onSubmit={formik.handleSubmit}>
          <table className="table">
            <thead
              style={{ backgroundColor: MecordColors.LightGrayColor }}
            >
              <tr>
                <th style={{ minWidth: "70px" }}>Sr No.</th>
                <th style={{ minWidth: "220px" }}>Model</th>
                <th style={{ minWidth: "220px" }}>Product</th>
                <th style={{ minWidth: "220px" }}>Unit</th>
                <th style={{ minWidth: "220px" }}>Unit Price</th>
                <th style={{ minWidth: "220px" }}>Lead Time (Weeks)</th>
                <th style={{ minWidth: "220px" }}>Capacity (per Month)</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {/* Add New Row */}
              <tr>
                <td className="align-middle">
                  <div className="d-flex align-items-center justify-content-center">
                    <b>New</b>
                  </div>
                </td>
                <td>
                  <Autocomplete
                    options={modelOptions}
                    getOptionLabel={(option: any) => option.model_name || ""}
                    value={formik.values.model}
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue("model", newValue || null);
                      formik.setFieldTouched("model", true);
                      formik.setFieldValue("product", null);
                      formik.setFieldValue("unit", null);
                      formik.setFieldValue("unit_price", "");
                    }}
                    onBlur={() => formik.setFieldTouched("model", true)}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.model_name,
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Model"
                        id="model"
                        name="model"
                        variant="outlined"
                        size="small"
                        error={!!(formik.errors.model && formik.touched.model)}
                        helperText={
                          formik.touched.model
                            ? (formik.errors.model as string)
                            : ""
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                  />
                </td>
                <td>
                  <Autocomplete
                    options={productOptions}
                    getOptionLabel={(option: any) => option.product_name || ""}
                    value={productOptions.find(
                      (opt: any) => opt.product_id === formik.values.product
                    )}
                    onChange={(event, newValue: any) => {
                      console.log("newValue", newValue);
                      formik.setFieldValue("product", newValue || null);
                      formik.setFieldValue(
                        "unit",
                        newValue?.unit?.unit_name || ""
                      );
                      formik.setFieldValue(
                        "unit_price",
                        newValue?.unit_price || ""
                      );
                    }}
                    onBlur={formik.handleBlur}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.product_name
                      )
                    }
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select product"
                        id="product"
                        name='product'
                        variant="outlined"
                        size="small"
                        error={
                          !!(formik.errors.product && formik.touched.product)
                        }
                        helperText={
                          formik.touched.product
                            ? (formik.errors.product as string)
                            : ""
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                  />
                </td>

                <td>
                  <Form.Control
                    readOnly
                    type="text"
                    name="unit"
                    placeholder="Autofill"
                    value={formik.values.unit || ""}
                  />
                </td>

                <td>
                  <Form.Control
                    type="text"
                    id="unit_price"
                    name="unit_price"
                    placeholder="Enter Unit Price"
                    value={formik.values.unit_price}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        formik.setFieldValue("unit_price", val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      formik.touched.unit_price && !!formik.errors.unit_price
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.unit_price}
                  </Form.Control.Feedback>
                </td>

                <td>
                  <Form.Control
                    type="text"
                    id="lead_time"
                    name="lead_time"
                    placeholder="Enter Lead Time (weeks)"
                    value={formik.values.lead_time}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        formik.setFieldValue("lead_time", val);
                      }
                    }}
                    isInvalid={
                      formik.touched.lead_time && !!formik.errors.lead_time
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.lead_time}
                  </Form.Control.Feedback>
                </td>

                <td>
                  <Form.Control
                    type="text"
                    id="capacity"
                    name="capacity"
                    placeholder="Enter Capacity"
                    value={formik.values.capacity}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        formik.setFieldValue("capacity", val);
                      }
                    }}
                    isInvalid={
                      formik.touched.capacity && !!formik.errors.capacity
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.capacity}
                  </Form.Control.Feedback>
                </td>

                <td className="text-center">
                  <PrimarySubmitButton type="submit" label="Add" />
                </td>
              </tr>

              {/* Existing Rows */}
              {products.map((item: any, index: number) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.model?.model_name || "-"}</td>
                  <td>{item.product?.product_name || "-"}</td>
                  <td>{item.unit?.unit_name || item.unit || "-"}</td>
                  <td>{formatIndianAmount(item.unit_price)}</td>
                  <td>{item.lead_time || "0"}</td>
                  <td>{item.capacity || "0"}</td>
                  <td className="text-center">
                    <DeleteButton onClick={() => handleDelete(index)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Form>
      </div>
      {/* Buttons */}
      <div className="d-flex flex-wrap justify-content-center gap-3 mt-5">
        <PrimarySubmitButton
          type="button"
          label="Prev"
          onClick={() => goToTab("branch")}
          className="px-4 mb-2"
        />
        <PrimarySubmitButton
          type="button"
          label="Submit"
          onClick={() => {
            final_submit();
          }}
          className="px-4 mb-2"
        />
      </div>
    </div>
  );
};

export default ProductDetails;
